import { useState, useEffect, useCallback } from 'react';
import type { AudioManager } from '../managers/AudioManager';

export type AudioManagerHookResult = {
  desktopVolume: number;
  micVolume: number;
  isDesktopUnmuted: boolean;
  isMicUnmuted: boolean;
  desktopDevices: { label: string; value: string; enabled: boolean }[];
  micDevices: { label: string; value: string; enabled: boolean }[];
  selectedDesktopDevice: string;
  selectedMicDevice: string;
  onDesktopVolumeChange: (volume: number) => void;
  onMicVolumeChange: (volume: number) => void;
  onDesktopMuteToggle: () => void;
  onMicMuteToggle: () => void;
  onDesktopDeviceChange: (deviceId: string) => void;
  onMicDeviceChange: (deviceId: string) => void;
  setDesktopVolume: (volume: number) => void;
  setMicVolume: (volume: number) => void;
};

export function useAudioManager(manager: AudioManager): AudioManagerHookResult {
  const store = manager.getStore();
  const [state, setState] = useState(store.currentState);

  useEffect(() => {
    setState(store.currentState);
    const id = store.addListener((s) => setState(s));
    return () => store.removeListener(id);
  }, [store]);

  const desktopDevices = state.desktopSources.map((s) => ({
    label: s.label,
    value: s.deviceId,
    enabled: true,
  }));

  const micDevices = state.micSources.map((s) => ({
    label: s.label,
    value: s.deviceId,
    enabled: true,
  }));

  const onDesktopVolumeChange = useCallback((volume: number) => {
    manager.setDesktopVolume(volume);
  }, [manager]);

  const onMicVolumeChange = useCallback((volume: number) => {
    manager.setMicVolume(volume);
  }, [manager]);

  const onDesktopMuteToggle = useCallback(() => {
    manager.setDesktopMuted(!store.currentState.desktopMuted);
  }, [manager, store]);

  const onMicMuteToggle = useCallback(() => {
    manager.setMicMuted(!store.currentState.micMuted);
  }, [manager, store]);

  const onDesktopDeviceChange = useCallback((deviceId: string) => {
    manager.selectDesktopSource(deviceId);
  }, [manager]);

  const onMicDeviceChange = useCallback((deviceId: string) => {
    manager.selectMicSource(deviceId);
  }, [manager]);

  const setDesktopVolume = useCallback((volume: number) => {
    manager.setDesktopVolume(volume);
  }, [manager]);

  const setMicVolume = useCallback((volume: number) => {
    manager.setMicVolume(volume);
  }, [manager]);

  return {
    desktopVolume: state.desktopVolume,
    micVolume: state.micVolume,
    isDesktopUnmuted: !state.desktopMuted,
    isMicUnmuted: !state.micMuted,
    desktopDevices,
    micDevices,
    selectedDesktopDevice: state.selectedDesktopDevice,
    selectedMicDevice: state.selectedMicDevice,
    onDesktopVolumeChange,
    onMicVolumeChange,
    onDesktopMuteToggle,
    onMicMuteToggle,
    onDesktopDeviceChange,
    onMicDeviceChange,
    setDesktopVolume,
    setMicVolume,
  };
}
